/**
 * Subscribe & Save Button Functionality
 * Handles subscription button clicks and redirects to product page with subscription options
 */

class SubscribeSave {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bindEvents());
    } else {
      this.bindEvents();
    }
  }

  bindEvents() {
    // Bind click events to all subscribe & save buttons
    const subscribeButtons = document.querySelectorAll('.subscribe-save-btn');
    subscribeButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleSubscribeClick(e));
    });

    // Bind dropdown close events
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('subscribe-save-dropdown-close')) {
        this.closeDropdown(e.target.closest('.subscribe-save-button'));
      }
    });

    // Bind subscription option selection events
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('subscribe-save-option') || e.target.closest('.subscribe-save-option')) {
        const option = e.target.classList.contains('subscribe-save-option') ? e.target : e.target.closest('.subscribe-save-option');
        this.handleOptionSelection(option);
      }
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.subscribe-save-button')) {
        document.querySelectorAll('.subscribe-save-dropdown').forEach(dropdown => {
          dropdown.style.display = 'none';
        });
      }
    });
  }

  handleSubscribeClick(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const subscriptionOptions = button.dataset.subscriptionOptions;

    // Don't proceed if button is disabled or product doesn't have subscription
    if (button.disabled || button.dataset.hasSubscription !== 'true') {
      this.showError('Subscription is not available for this product.');
      return;
    }

    // If there are subscription options, show dropdown for selection
    if (subscriptionOptions && subscriptionOptions.trim() !== '') {
      this.showDropdown(button);
      return;
    }

    // If no options, proceed with default behavior
    this.processSubscription(button);
  }

  processSubscription(button, selectedPlanId = null) {
    const productId = button.dataset.productId;
    const productHandle = button.dataset.productHandle;
    const hasSubscription = button.dataset.hasSubscription === 'true';
    const discountPercentage = button.dataset.discountPercentage;
    const discountType = button.dataset.discountType || 'percentage';
    const rechargeWidgetId = button.dataset.rechargeWidgetId;

    console.log('Processing subscription:', {
      productId,
      productHandle,
      selectedPlanId,
      hasSubscription,
      rechargeWidgetId
    });

    // Show loading state
    this.setButtonLoading(button, true);

    // Always try to add to cart directly, regardless of current page
    if (rechargeWidgetId && selectedPlanId) {
      // Use Recharge-specific handling with selected plan
      this.addSubscriptionToCartDirect(productId, selectedPlanId, button);
    } else if (selectedPlanId) {
      // Use standard subscription handling with selected plan
      this.addSubscriptionToCart(productId, discountPercentage, button, selectedPlanId);
    } else {
      // If no plan selected and we're not on product page, redirect
      if (!window.location.pathname.includes('/products/')) {
        this.redirectToProductPage(productHandle, discountPercentage, discountType);
      } else {
        // Try to handle on product page
        if (rechargeWidgetId) {
          this.handleRechargeSubscription(productId, rechargeWidgetId, discountPercentage, discountType, button, selectedPlanId);
        } else {
          this.addSubscriptionToCart(productId, discountPercentage, button, selectedPlanId);
        }
      }
    }
  }

  async addSubscriptionToCart(productId, discountPercentage, button, selectedPlanId = null) {
    try {
      // Get product handle from button data
      const productHandle = button.dataset.productHandle;
      
      // First, get the product data to find subscription selling plans
      const productResponse = await fetch(`/products/${productHandle}.js`);
      
      if (!productResponse.ok) {
        throw new Error(`Failed to fetch product data: ${productResponse.status}`);
      }
      
      const product = await productResponse.json();

      // Use selected plan ID or find the first available subscription selling plan
      let subscriptionPlanId = selectedPlanId;
      
      if (!subscriptionPlanId && product.selling_plan_groups && product.selling_plan_groups.length > 0) {
        const subscriptionGroup = product.selling_plan_groups.find(group => 
          group.name.toLowerCase().includes('subscription') || 
          group.name.toLowerCase().includes('subscribe')
        );
        
        if (subscriptionGroup && subscriptionGroup.selling_plans.length > 0) {
          subscriptionPlanId = subscriptionGroup.selling_plans[0].id;
        }
      }

      if (!subscriptionPlanId) {
        throw new Error('No subscription plan found for this product');
      }

      // Add to cart with subscription
      const cartData = {
        items: [{
          id: product.variants[0].id,
          quantity: 1,
          selling_plan: subscriptionPlanId
        }]
      };

      const cartResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData)
      });

      if (cartResponse.ok) {
        this.showSuccess('Product added to cart with subscription!');
        // Trigger cart update event if available
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } else {
        throw new Error('Failed to add to cart');
      }

    } catch (error) {
      console.error('Subscription add to cart error:', error);
      console.error('Product ID:', productId);
      console.error('Product Handle:', button.dataset.productHandle);
      console.error('Selected Plan ID:', selectedPlanId);
      this.showError(`Unable to add subscription: ${error.message}`);
    } finally {
      this.setButtonLoading(button, false);
    }
  }

  async addSubscriptionToCartDirect(productId, selectedPlanId, button) {
    try {
      // Get product handle from button data
      const productHandle = button.dataset.productHandle;
      
      // Get the product data to find the default variant
      const productResponse = await fetch(`/products/${productHandle}.js`);
      
      if (!productResponse.ok) {
        throw new Error(`Failed to fetch product data: ${productResponse.status}`);
      }
      
      const product = await productResponse.json();

      if (!product.variants || product.variants.length === 0) {
        throw new Error('No variants found for this product');
      }

      // Use the first available variant or the default variant
      const variant = product.variants.find(v => v.available) || product.variants[0];

      // Add to cart with the selected subscription plan
      const cartData = {
        items: [{
          id: variant.id,
          quantity: 1,
          selling_plan: parseInt(selectedPlanId)
        }]
      };

      const cartResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData)
      });

      if (cartResponse.ok) {
        this.showSuccess('Product added to cart with subscription!');
        // Trigger cart update event if available
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } else {
        const errorData = await cartResponse.json();
        throw new Error(errorData.message || 'Failed to add to cart');
      }

    } catch (error) {
      console.error('Direct subscription add to cart error:', error);
      console.error('Product ID:', productId);
      console.error('Product Handle:', button.dataset.productHandle);
      console.error('Selected Plan ID:', selectedPlanId);
      this.showError(`Unable to add subscription: ${error.message}`);
    } finally {
      this.setButtonLoading(button, false);
    }
  }

  handleRechargeSubscription(productId, rechargeWidgetId, discountPercentage, discountType, button, selectedPlanId = null) {
    try {
      // Check if Recharge is available
      if (typeof window.ReCharge === 'undefined' && typeof window.rc === 'undefined') {
        console.warn('Recharge not found, falling back to standard subscription');
        this.addSubscriptionToCart(productId, discountPercentage, button);
        return;
      }

      // If we have a selected plan ID, add directly to cart
      if (selectedPlanId) {
        this.addSubscriptionToCartDirect(productId, selectedPlanId, button);
        return;
      }

      // Try to trigger Recharge subscription selection
      // Method 1: Try to find and click the Recharge subscription option
      const rechargeContainer = document.querySelector('.rc-container-wrapper');
      if (rechargeContainer) {
        const subscriptionOption = rechargeContainer.querySelector('.rc_widget__option--subsave');
        if (subscriptionOption) {
          // Select the subscription option
          const radioInput = subscriptionOption.querySelector('.rc-radio__input');
          if (radioInput) {
            radioInput.checked = true;
            radioInput.dispatchEvent(new Event('change', { bubbles: true }));
            
            // Try to add to cart after a short delay to allow Recharge to process
            setTimeout(() => {
              this.triggerAddToCartWithNotification(button);
            }, 500);
            
            this.showSuccess('Subscription option selected! Adding to cart...');
            this.setButtonLoading(button, false);
            return;
          }
        }
      }

      // Method 2: If we can't find the widget, redirect to product page
      console.warn('Recharge widget not found on current page, redirecting to product page');
      const productHandle = button.dataset.productHandle;
      this.redirectToProductPage(productHandle, discountPercentage, discountType);
      
    } catch (error) {
      console.error('Recharge subscription error:', error);
      this.showError('Unable to set up subscription. Please try again on the product page.');
      this.setButtonLoading(button, false);
    }
  }

  triggerAddToCart(button) {
    // Try to find and click the main add to cart button
    const addToCartButton = document.querySelector('[name="add"], .product-form__cart-submit, .btn-product-add-to-cart');
    if (addToCartButton && !addToCartButton.disabled) {
      addToCartButton.click();
    } else {
      this.showError('Please add the product to cart manually with your subscription selection.');
    }
  }

  triggerAddToCartWithNotification(button) {
    // Try to find and click the main add to cart button
    const addToCartButton = document.querySelector('[name="add"], .product-form__cart-submit, .btn-product-add-to-cart');
    if (addToCartButton && !addToCartButton.disabled) {
      // Listen for successful cart addition
      const handleCartUpdate = () => {
        this.showSuccess('Product added to cart with subscription!');
        document.removeEventListener('cart:updated', handleCartUpdate);
      };
      
      document.addEventListener('cart:updated', handleCartUpdate);
      addToCartButton.click();
    } else {
      this.showError('Please add the product to cart manually with your subscription selection.');
      this.setButtonLoading(button, false);
    }
  }

  showDropdown(button) {
    // Close any other open dropdowns
    document.querySelectorAll('.subscribe-save-dropdown').forEach(dropdown => {
      dropdown.style.display = 'none';
    });

    // Show the dropdown for this button
    const dropdown = button.parentElement.querySelector('.subscribe-save-dropdown');
    if (dropdown) {
      dropdown.style.display = 'block';
    }
  }

  closeDropdown(buttonContainer) {
    const dropdown = buttonContainer.querySelector('.subscribe-save-dropdown');
    if (dropdown) {
      dropdown.style.display = 'none';
    }
  }

  handleOptionSelection(optionButton) {
    const planId = optionButton.dataset.planId;
    const planName = optionButton.dataset.planName;
    const discount = optionButton.dataset.discount;
    
    // Find the main subscribe button
    const mainButton = optionButton.closest('.subscribe-save-button').querySelector('.subscribe-save-btn');
    
    // Close the dropdown
    this.closeDropdown(optionButton.closest('.subscribe-save-button'));
    
    // Process the subscription with the selected plan
    this.processSubscription(mainButton, planId);
  }

  redirectToProductPage(productHandle, discountPercentage, discountType = 'percentage') {
    // Redirect to product page with subscription parameters
    const url = `/products/${productHandle}?subscription=true&discount=${discountPercentage}&discount_type=${discountType}`;
    window.location.href = url;
  }

  setButtonLoading(button, isLoading) {
    if (isLoading) {
      // Store original content if not already stored
      if (!button.getAttribute('data-original-content')) {
        button.setAttribute('data-original-content', button.innerHTML);
      }
      
      button.disabled = true;
      button.innerHTML = '<div class="subscribe-save-button-content"><span class="subscribe-save-text">Adding...</span></div>';
      button.classList.add('loading');
    } else {
      button.disabled = false;
      // Restore original button content
      const originalContent = button.getAttribute('data-original-content');
      if (originalContent) {
        button.innerHTML = originalContent;
      }
      button.classList.remove('loading');
    }
  }

  showSuccess(message) {
    this.showNotification(message, 'success');
  }

  showError(message) {
    this.showNotification(message, 'error');
  }

  showNotification(message, type) {
    // Create a simple notification
    const notification = document.createElement('div');
    notification.className = `subscribe-save-notification subscribe-save-notification--${type}`;
    notification.textContent = message;
    
    // Style the notification
    Object.assign(notification.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '4px',
      color: 'white',
      backgroundColor: type === 'success' ? '#4caf50' : '#f44336',
      zIndex: '10000',
      fontSize: '14px',
      fontWeight: 'bold',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
    });

    document.body.appendChild(notification);

    // Remove notification after 3 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 3000);
  }
}

// Initialize when script loads
new SubscribeSave();
